import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import type { IEntityDataItem, TEntityData, TEntityId } from '~/types/models'
import type {
  IPerson,
  IPersonMatchCandidate,
  TPersonAliasKind,
} from '~/types/person/models'
import type { TContact } from '~/types/contact/models'
import type { ITeamMember } from '~/types/teamMember/models'
import type { IUser } from '~/types/user/models'
import { Contact } from '~/cynqueModules/dataObjects/Contact'
import { Person } from '~/cynqueModules/dataObjects/Person'
import { TeamMember } from '~/cynqueModules/dataObjects/TeamMember'
import { User } from '~/cynqueModules/dataObjects/User'

const apiEntityPath = '/api/persons'

/**
 * A Cynque bot -- Case Bot today, more expected. See IPerson.isSystem, which is
 * where the flag lives and the only place it lives.
 *
 * 🔑 This is the one definition of "not a human". A team member, a user and all
 * four contact kinds are BINDINGS of a person, so they inherit it rather than
 * carrying their own copy: isSystemTeamMember (services/teamMembers.ts) reads it
 * off the embedded person through here.
 */
export function isSystemPerson(person: IPerson): boolean {
  return person.isSystem === true
}

/**
 * Every person, including the bots'.
 *
 * ⚠ For the /Persons LIST, not for pickers. Use getOfferablePersons anywhere a
 * user chooses a person, or they can attach a contact, a user or a team member
 * to Cynque. The list deliberately still shows it: hiding the row would leave an
 * author on notes with nothing to explain it, which is the same call made for
 * team members in services/teamMembers.ts.
 */
export async function getPersons({ apiPath = apiEntityPath } = {}) {
  const callingMn = getPersons.name
  const rawPersons = await httpGet<IPerson[]>({ callingMn, apiPath })
  return setUpPersons(rawPersons)
}

/**
 * The persons a user may CHOOSE. This is the chokepoint the seven person select
 * stanzas under entityInputFieldsService feed from.
 */
export async function getOfferablePersons({ apiPath = apiEntityPath } = {}) {
  const persons = await getPersons({ apiPath })
  return persons.filter((person) => !isSystemPerson(person))
}

export async function getContactsForPersonId(id: TEntityId) {
  const callingMn = getContactsForPersonId.name
  const apiPath = `${apiEntityPath}/${id}/contacts`
  const contacts = await httpGet<TContact[]>({ callingMn, apiPath })
  contacts.forEach((contact) => {
    contact.type = Contact(contact).type
    contact.parentName = Contact(contact).parentName
  })
  return contacts
}

export async function getContactsAsAliasesForPersonId(id: TEntityId) {
  const callingMn = getContactsForPersonId.name
  const apiPath = `${apiEntityPath}/${id}/contacts`
  const contacts = await httpGet<TContact[]>({ callingMn, apiPath })
  contacts.forEach((contact) => {
    contact = Object.assign(contact, Contact(contact))
    contact.parentName = Contact(contact).parentName
  })
  return contacts
}

export async function getTeamMembersAsAliasesForPersonId(id: TEntityId) {
  const callingMn = getTeamMembersAsAliasesForPersonId.name
  const apiPath = `${apiEntityPath}/${id}/teammembers`
  const teamMembers = await httpGet<ITeamMember[]>({ callingMn, apiPath })
  teamMembers.forEach((teamMember) => {
    teamMember = Object.assign(teamMember, TeamMember(teamMember))
    teamMember.type = { id: 999, label: 'Team Member' }
    teamMember.parentName = 'WXTG'
  })
  return teamMembers
}

export async function getUsersAsAliasesForPersonId(id: TEntityId) {
  const callingMn = getUsersAsAliasesForPersonId.name
  const apiPath = `${apiEntityPath}/${id}/users`
  const Users = await httpGet<IUser[]>({ callingMn, apiPath })
  Users.forEach((user) => {
    user = Object.assign(user, User(user))
    user.type = { id: 999, label: 'User' }
    user.parentName = user.primaryGroup.label
  })
  return Users
}

export async function getAliasesForPersonId(id: TEntityId) {
  const [contacts, teamMembers, users] = await Promise.all([
    getContactsAsAliasesForPersonId(id),
    getTeamMembersAsAliasesForPersonId(id),
    getUsersAsAliasesForPersonId(id),
  ])

  contacts.forEach(
    (contact) => (contact.type.label = `${contact.type.label} Contact`)
  )
  // Tag each row with the source it came from while that is still known. After
  // the concatenation below the three kinds are indistinguishable, and
  // PersonAliasesCard needs the kind to decide where a row click navigates.
  contacts.forEach((contact) => {
    contact.aliasKind = _contactAliasKind(contact)
  })
  _tagAliasKind(teamMembers, 'teamMember')
  _tagAliasKind(users, 'user')
  const links = [...contacts, ...teamMembers, ...users]
  return links
}

/**
 * Which parent a contact hangs off, mirroring the same four-way test (and its
 * order) that `Contact()` uses to derive `type` and `avatar.type`. A contact
 * always has exactly one of these; `Contact()` throws when it has none.
 */
function _contactAliasKind(contact: TContact): TPersonAliasKind {
  if (contact.company) {
    return 'companyContact'
  }
  if (contact.supplier) {
    return 'supplierContact'
  }
  if (contact.tsd) {
    return 'tsdContact'
  }
  return 'leadSourceContact'
}

function _tagAliasKind(rows: IEntityDataItem[], kind: TPersonAliasKind): void {
  for (const row of rows) {
    row.aliasKind = kind
  }
}

/**
 * Ranked existing persons whose identity keys intersect the input, for the
 * "did you mean…?" prompt (cynque-api-server#1539). Suggest-only: the server
 * never writes and returns [] when nothing matches. Omitted/blank fields are
 * simply not sent.
 */
export async function matchPersons(input: {
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  limit?: number
}) {
  const callingMn = matchPersons.name
  const params = new URLSearchParams()
  const add = (key: string, value: string | number | undefined) => {
    const s = value === undefined ? '' : String(value).trim()
    if (s) {
      params.set(key, s)
    }
  }
  add('email', input.email)
  add('phone', input.phone)
  add('firstName', input.firstName)
  add('lastName', input.lastName)
  add('limit', input.limit)
  const query = params.toString()
  // No identifying input → no server round-trip, no candidates.
  if (!['email', 'phone', 'firstName', 'lastName'].some((k) => params.has(k))) {
    return [] as IPersonMatchCandidate[]
  }
  const apiPath = `${apiEntityPath}/match?${query}`
  return httpGet<IPersonMatchCandidate[]>({ callingMn, apiPath })
}

/**
 * Merge the loser person into the survivor (cynque-api-server#1539). Re-points
 * every reference, fills the survivor's empty fields, and deletes the loser;
 * `version` is the survivor's concurrency token (a stale value yields 409).
 * Returns the merged survivor.
 */
export async function mergePersons({
  survivorId,
  loserId,
  version,
}: {
  survivorId: TEntityId
  loserId: TEntityId
  version: string
}) {
  const callingMn = mergePersons.name
  const apiPath = `${apiEntityPath}/${survivorId}/merge`
  // The merge body is {loserId, version}, not an entity; httpPost JSON-stringifies
  // it as-is (buildPostBody only special-cases File payloads).
  const entity = { loserId, version } as unknown as TEntityData
  const survivor = await httpPost<TEntityData>({ callingMn, apiPath, entity })
  return Person(survivor as unknown as IPerson)
}

export async function getPersonById(id: TEntityId) {
  const callingMn = getPersonById.name
  const apiPath = `${apiEntityPath}/${id}`
  const rawPerson = await httpGet<IPerson>({ callingMn, apiPath })
  return Person(rawPerson)
}

export async function createPerson(entity: IPerson) {
  const callingMn = createPerson.name
  const apiPath = `/api/persons/`
  return httpPost<IPerson>({ callingMn, apiPath, entity })
}

export async function updatePerson(
  entityData: Partial<IPerson>,
  id: TEntityId
) {
  const callingMn = updatePerson.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialIPerson = Partial<IPerson>
  return httpPatchMerge<TPartialIPerson>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deletePerson(entityData: IPerson) {
  const callingMn = deletePerson.name
  const apiPath = apiEntityPath
  return httpDelete<IPerson>({
    callingMn,
    apiPath,
    entityData,
  })
}

// Helper functions

export function setUpPersons(rawPersons: IPerson[]) {
  return rawPersons.map((rawPerson) => {
    return Person(rawPerson)
  })
}
